import { Fragment, useMemo } from 'react';

import { VSpacing } from '@hh.ru/magritte-ui';
import { LanguageLevel } from '@hh.ru/types-hh-microcore';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import translation from 'src/components/translation';
import type { ResumeCardData } from 'src/models/resumeCard';

import CollapseCollection from 'src/components/ResumeCard/Body/Collapse/Collection';
import Field from 'src/components/ResumeCard/Body/Field';

const TrlKeys = {
    title: 'resumeCard.field.languages',
    text: {
        [LanguageLevel.L1]: 'employer.resumesSearch.nativeLanguage',
        [LanguageLevel.A1]: 'employer.resumesSearch.language.a1',
        [LanguageLevel.A2]: 'employer.resumesSearch.language.a2',
        [LanguageLevel.B1]: 'employer.resumesSearch.language.b1',
        [LanguageLevel.B2]: 'employer.resumesSearch.language.b2',
        [LanguageLevel.C1]: 'employer.resumesSearch.language.c1',
        [LanguageLevel.C2]: 'employer.resumesSearch.language.c2',
    },
};

const Language: TranslatedComponent<{ resume: ResumeCardData }> = ({ resume, trls }) => {
    const languages = resume.language;
    const sortedLanguages = useMemo(() => {
        if (!languages?.length) {
            return [];
        }
        return languages.sort((langA, langB) => {
            if (langA.degree === LanguageLevel.L1) {
                return -1;
            }
            if (langB.degree === LanguageLevel.L1) {
                return 1;
            }
            return langA.trl.localeCompare(langB.trl);
        });
    }, [languages]);

    if (!sortedLanguages.length) {
        return null;
    }
    return (
        <Field title={trls[TrlKeys.title]} dataQaName="languages">
            <CollapseCollection
                collection={sortedLanguages}
                renderItem={({ id, degree, trl }, index) => {
                    const text =
                        degree === LanguageLevel.L1
                            ? `${trls[TrlKeys.text[degree]]} — ${trl}`
                            : `${trl}, ${trls[TrlKeys.text[degree]]}`;
                    return (
                        <Fragment key={id}>
                            {index !== 0 && <VSpacing default={8} />}
                            <span>{text}</span>
                        </Fragment>
                    );
                }}
                dataQa="languages"
            />
        </Field>
    );
};

export default translation(Language);
